import React, { useEffect, useMemo, useState } from 'react';

import {

  Alert, Box, Button, Chip, DialogActions, DialogContent, DialogTitle, IconButton,

  InputAdornment, MenuItem, OutlinedInput, Paper, Select, Stack, Table, TableBody,

  TableCell, TableHead, TableRow, Tooltip, Typography,

} from '@mui/material';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { alpha } from '@mui/material/styles';



import AppDialog from '../../../shared/components/AppDialog';

import LabeledField from '../../../shared/components/LabeledField';

import { SCROLLABLE_SELECT_MENU_PROPS } from '../../../shared/components/selectMenuProps';

import { formatNumber } from '../../../shared/utils/number';

import { managementReportingYearOptions, reportingCalendarYear } from '../../../shared/utils/reportingYear';

import { SCOPE_TOKENS } from '../../../theme/muiTheme';

import { DEMO_INVENTORIES, DEMO_TARGET_CYCLES } from '../model/climateActionDemo';

import {

  baselineEmissionsFor, createTargetDrafts, DEFAULT_BASE_YEAR, TARGET_ERROR_CODES, TARGET_SCOPES,

  TARGET_YEAR_FORWARD_SPAN, updateDraftReduction, updateDraftTargetYear, validateTargetDraft,

} from './targetDialogModel';

import { normalizeTargetCycles, TARGET_CYCLE_STATUS } from './targetCycleModel';



const COPY = {

  subtitle: 'Set the final reduction for each Scope, then review the annual pathway before saving.',

  periodName: 'Period name',

  baselineYear: 'Baseline FY',

  targetYear: 'Final target FY',

  baselinePending: 'Pending',

  baselineAvailable: 'Available',

  baselinePendingHint: 'Percent targets can be saved now. Absolute tCO₂e values will appear after this FY is verified.',

  scopeTargets: 'Reduction pathway by Scope',

  scopeTargetsHint: 'Use Automatic for a linear pathway or Custom to enter each year.',

  pathwayMode: 'Pathway method',

  automaticMode: 'Automatic pathway',

  automaticModeHint: 'Set the final reduction and generate annual targets.',

  customMode: 'Custom annual pathway',

  customModeHint: 'Enter the reduction percentage for every year.',

  reductionTarget: 'Final reduction',

  customAnnualTitle: 'Annual reduction pathway',

  customAnnualHint: 'Automatic values are calculated. Edit Custom values directly.',

  baselineValue: 'Baseline emissions',

  finalValue: 'Calculated final target',

  pendingValue: 'Calculated after baseline is available',

  year: 'FY',

  final: 'Final',

  finalTargetEstimate: 'Estimated final target',

  save: 'Save target',

  cancel: 'Cancel',

  errors: {

    [TARGET_ERROR_CODES.TARGET_YEAR]: 'Final target FY must be after the baseline FY.',

    [TARGET_ERROR_CODES.TARGET_RANGE]: 'Target period cannot be longer than 50 years.',

    [TARGET_ERROR_CODES.REDUCTION]: 'Enter a reduction greater than 0% and no more than 100%.',

  },

};



const PATHWAY_MODES = { AUTOMATIC: 'automatic', CUSTOM: 'custom' };



const copy = (t, key, defaultValue, options = {}) => t(key, { defaultValue, ...options });

const scopeTokenFor = scope => ({

  scope1: SCOPE_TOKENS.SCOPE_1,

  scope2: SCOPE_TOKENS.SCOPE_2,

  scope3: SCOPE_TOKENS.SCOPE_3,

}[scope] || SCOPE_TOKENS.TOTAL);



function NumberInput({ adornment, ariaLabel, disabled = false, id, inputProps, onChange, value }) {

  return (

    <OutlinedInput

      endAdornment={<InputAdornment position='end'>{adornment}</InputAdornment>}

      disabled={disabled}

      id={id}

      inputProps={{ ...inputProps, 'aria-label': ariaLabel }}

      onChange={onChange}

      size='small'

      sx={{ bgcolor: 'background.paper', minWidth: 0 }}

      type='number'

      value={value}

    />

  );

}


function ScopeReductionTable({ drafts, modes, onModeChange, onReductionChange, readOnly = false, t }) {

  return (

    <Paper variant='outlined' sx={{ overflow: 'hidden' }}>

      <Box sx={{ bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider', px: 2, py: 1.25 }}>

        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent='space-between' spacing={1}>

          <Box>

            <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>{copy(t, 'climateAction.target.scopeTargets', COPY.scopeTargets)}</Typography>

            <Typography color='text.secondary' variant='caption'>{copy(t, 'climateAction.target.scopeTargetsHint', COPY.scopeTargetsHint)}</Typography>

          </Box>

        </Stack>

      </Box>

      <Box sx={{ overflowX: 'auto' }}>

        <Table size='small' sx={{ minWidth: 560 }}>

          <TableHead>

            <TableRow>

              <TableCell sx={{ fontWeight: 700 }}>Scope</TableCell>

              <TableCell sx={{ fontWeight: 700 }}>{copy(t, 'climateAction.target.pathwayMode', COPY.pathwayMode)}</TableCell>

              <TableCell align='right' sx={{ fontWeight: 700 }}>{copy(t, 'climateAction.target.reductionTarget', COPY.reductionTarget)}</TableCell>

            </TableRow>

          </TableHead>

          <TableBody>

            {TARGET_SCOPES.map(scope => {

              const token = scopeTokenFor(scope);

              const draft = drafts[scope];

              const scopeLabel = t(`climateAction.scope.${scope}`);

              return (

                <TableRow key={scope}>

                  <TableCell>

                    <Stack direction='row' alignItems='center' spacing={0.8}>

                      <Box sx={{ bgcolor: token.color, borderRadius: 4, height: 18, width: 4 }} />

                      <Typography sx={{ color: 'text.primary', fontWeight: 700 }}>{scopeLabel}</Typography>

                    </Stack>

                  </TableCell>

                  <TableCell sx={{ minWidth: 220 }}>

                    <Select

                      fullWidth

                      disabled={readOnly}

                      MenuProps={SCROLLABLE_SELECT_MENU_PROPS}

                      onChange={event => onModeChange(scope, event.target.value)}

                      size='small'

                      value={modes[scope]}

                    >

                      <MenuItem value={PATHWAY_MODES.AUTOMATIC}>{copy(t, 'climateAction.target.automaticMode', COPY.automaticMode)}</MenuItem>

                      <MenuItem value={PATHWAY_MODES.CUSTOM}>{copy(t, 'climateAction.target.customMode', COPY.customMode)}</MenuItem>

                    </Select>

                  </TableCell>

                  <TableCell align='right' sx={{ width: 190 }}>

                    {modes[scope] === PATHWAY_MODES.AUTOMATIC ? (

                      <NumberInput

                        adornment='%'

                        ariaLabel={`${scopeLabel} ${COPY.reductionTarget}`}

                        id={`target-reduction-${scope}`}

                        inputProps={{ min: 0.01, max: 100, step: 0.1 }}

                        onChange={event => onReductionChange(scope, event.target.value)}

                        disabled={readOnly}

                        value={draft.reductionPercent === '' ? '' : Number(Number(draft.reductionPercent).toFixed(1))}

                      />

                    ) : (

                      <Typography color='text.secondary' variant='caption' sx={{ fontWeight: 650 }}>Edit below</Typography>

                    )}

                  </TableCell>

                </TableRow>

              );

            })}

          </TableBody>

        </Table>

      </Box>

    </Paper>

  );

}



function PeriodSettings({ baseYear, baselineYears = [], errors, onBaselineYearChange, onTargetYearChange, readOnly = false, targetYear, t }) {

  const currentYear = reportingCalendarYear();

  const baselineOptions = (baselineYears.length > 0
    ? baselineYears
    : managementReportingYearOptions({ yearsAhead: 1 }))

    .map(Number)

    .filter(year => Number.isInteger(year) && (!Number.isInteger(Number(targetYear)) || year <= Number(targetYear)))

    .sort((left, right) => left - right);

  const targetOptions = Array.from(

    { length: Number.isInteger(Number(baseYear))
      ? (currentYear + TARGET_YEAR_FORWARD_SPAN) - Number(baseYear)
      : 0 },

    (_, index) => Number(baseYear) + index + 1,

  ).filter(year => year >= Number(baseYear));

  const targetError = errors.find(code => (

    code === TARGET_ERROR_CODES.TARGET_YEAR || code === TARGET_ERROR_CODES.TARGET_RANGE

  ));



  return (

    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>

      <LabeledField htmlFor='target-baseline-year' label={copy(t, 'climateAction.target.baseYear', COPY.baselineYear)} required>

        <Select

          fullWidth

          disabled={readOnly}

          id='target-baseline-year'

          MenuProps={SCROLLABLE_SELECT_MENU_PROPS}

          onChange={event => onBaselineYearChange(event.target.value)}

          size='small'

          value={Number.isInteger(Number(baseYear)) ? Number(baseYear) : ''}

        >

          {baselineOptions.map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}

        </Select>

      </LabeledField>

      <LabeledField

        error={targetError ? copy(t, `climateAction.target.error.${targetError}`, COPY.errors[targetError]) : undefined}

        htmlFor='shared-target-year'

        label={copy(t, 'climateAction.target.targetYear', COPY.targetYear)}

        required

      >

        <Select

        fullWidth

        disabled={readOnly}

          id='shared-target-year'

          MenuProps={SCROLLABLE_SELECT_MENU_PROPS}

          onChange={event => onTargetYearChange(event.target.value)}

          size='small'

          value={Number(targetYear) || ''}

        >

          {targetOptions.map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}

        </Select>

      </LabeledField>

    </Box>

  );

}



function pathwayYears(drafts) {

  const firstDraft = drafts.scope1;

  if (!firstDraft) return [];

  const baselineYear = Number(firstDraft.baselineYear);

  const targetYear = Number(firstDraft.targetYear);

  if (!Number.isInteger(baselineYear) || !Number.isInteger(targetYear) || targetYear < baselineYear) return [];

  return Array.from({ length: targetYear - baselineYear + 1 }, (_, index) => baselineYear + index);

}



function buildAnnualReductions(drafts, previous = {}) {

  return Object.fromEntries(TARGET_SCOPES.map(scope => {

    const draft = drafts[scope];

    const years = pathwayYears(drafts);

    const duration = Number(draft?.targetYear) - Number(draft?.baselineYear);

    const finalReduction = Number(draft?.reductionPercent) || 0;

    const previousScope = previous[scope] || {};

    const values = Object.fromEntries(years.map(year => {

      const progress = duration > 0 ? (year - Number(draft.baselineYear)) / duration : 0;

      const fallback = Math.max(0, Math.min(100, finalReduction * progress));

      const savedValue = draft?.annualReductionPercentages?.[year];

      return [year, previousScope[year] ?? savedValue ?? Number(fallback.toFixed(2))];

    }));

    return [scope, values];

  }));

}



function reductionAtYear(draft, scope, year, modes, annualReductions) {

  if (modes[scope] === PATHWAY_MODES.CUSTOM) {

    return Number(annualReductions?.[scope]?.[year] ?? 0);

  }

  const duration = Number(draft.targetYear) - Number(draft.baselineYear);

  const progress = duration > 0 ? (year - Number(draft.baselineYear)) / duration : 0;

  return Math.max(0, Math.min(100, (Number(draft.reductionPercent) || 0) * progress));

}



function CustomAnnualTable({ annualReductions, drafts, modes, onAnnualChange, readOnly = false, t }) {

  const customScopes = TARGET_SCOPES.filter(scope => modes[scope] === PATHWAY_MODES.CUSTOM);

  const years = pathwayYears(drafts);

  const hasIncompleteValues = customScopes.some(scope => years.some(year => (

    year > Number(drafts[scope].baselineYear)

    && (annualReductions[scope]?.[year] === ''

      || !Number.isFinite(Number(annualReductions[scope]?.[year]))

      || Number(annualReductions[scope]?.[year]) < 0

      || Number(annualReductions[scope]?.[year]) > 100)

  )));

  return (

    <Paper variant='outlined' sx={{ overflow: 'hidden' }}>

      <Box sx={{ bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider', px: 2, py: 1.2 }}>

        <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>{copy(t, 'climateAction.target.customAnnualTitle', COPY.customAnnualTitle)}</Typography>

        <Typography color='text.secondary' variant='caption'>{copy(t, 'climateAction.target.customAnnualHint', COPY.customAnnualHint)}</Typography>

      </Box>

      <Box sx={{ overflowX: 'auto' }}>

        <Table size='small' sx={{ minWidth: 620 }}>

          <TableHead><TableRow><TableCell sx={{ fontWeight: 700 }}>Year</TableCell>{TARGET_SCOPES.map(scope => <TableCell key={scope} align='right' sx={{ fontWeight: 700 }}>{t(`climateAction.scope.${scope}`)}</TableCell>)}</TableRow></TableHead>

          <TableBody>{years.map(year => {

            const baseline = year === Number(drafts.scope1.baselineYear);

            return <TableRow key={year} sx={theme => ({ bgcolor: baseline ? alpha(theme.palette.info.main, 0.05) : undefined })}>

              <TableCell sx={{ fontWeight: baseline || year === Number(drafts.scope1.targetYear) ? 850 : 650 }}>{year}{baseline && <Chip label='Baseline' size='small' sx={{ ml: 0.75, height: 20 }} />}</TableCell>

              {TARGET_SCOPES.map(scope => <TableCell key={scope} align='right'>

                {modes[scope] === PATHWAY_MODES.CUSTOM && !baseline && !readOnly ? <NumberInput adornment='%' ariaLabel={`${t(`climateAction.scope.${scope}`)} ${year} reduction`} id={`annual-reduction-${scope}-${year}`} inputProps={{ min: 0, max: 100, step: 0.1 }} onChange={event => onAnnualChange(scope, year, event.target.value)} value={annualReductions[scope]?.[year] ?? ''} /> : <Typography color={baseline ? 'text.secondary' : 'text.primary'} sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: baseline ? 600 : 750 }}>{baseline ? '0.0%' : `−${formatNumber(reductionAtYear(drafts[scope], scope, year, modes, annualReductions), { min: 1, max: 1 })}%`}</Typography>}

              </TableCell>)}

            </TableRow>;

          })}</TableBody>

        </Table>

      </Box>

      {hasIncompleteValues && <Typography color='error.main' variant='caption' sx={{ display: 'block', px: 2, py: 1 }}>Enter a percentage from 0% to 100% for every custom year before saving.</Typography>}

    </Paper>

  );

}



function AbsolutePreview({ drafts, inventories, t }) {

  const values = TARGET_SCOPES.map(scope => baselineEmissionsFor(drafts[scope], inventories));

  if (!values.every(value => Number.isFinite(value))) return null;

  const baselineTotal = values.reduce((sum, value) => sum + value, 0);

  const targetTotal = TARGET_SCOPES.reduce((sum, scope, index) => sum + values[index] * (1 - (Number(drafts[scope].reductionPercent) / 100)), 0);

  return (

    <Stack alignItems={{ xs: 'flex-start', sm: 'center' }} direction={{ xs: 'column', sm: 'row' }} spacing={0.45} sx={{ px: 0.5 }}>

      <Typography color='text.secondary' variant='caption'>

        {copy(t, 'climateAction.target.finalTargetEstimate', COPY.finalTargetEstimate)}

      </Typography>

      <Typography sx={{ color: 'success.dark', fontFamily: '"IBM Plex Mono", monospace', fontSize: 14, fontWeight: 750 }}>

        {formatNumber(targetTotal, { min: 2, max: 2 })} tCO₂e

      </Typography>

      <Tooltip arrow placement='top' title={`Baseline ${formatNumber(baselineTotal, { min: 2, max: 2 })} tCO₂e. Each Scope target = baseline emissions × (1 − final reduction %).`}>

        <IconButton aria-label='Final target calculation details' size='small' sx={{ color: 'text.secondary', p: 0.25 }}>

          <InfoOutlinedIcon sx={{ fontSize: 15 }} />

        </IconButton>

      </Tooltip>

    </Stack>

  );

}



/** Configure one target period with either an automatic or custom annual pathway. */

export default function TargetDialog({ baselineYears = [], cycleId, inventories = DEMO_INVENTORIES, mode = 'edit', onClose, onSave, open, readOnly = false, t, targetCycles = DEMO_TARGET_CYCLES, targets = {} }) {

  const normalizedCycles = useMemo(() => normalizeTargetCycles(targetCycles, targets), [targetCycles, targets]);

  const activeCycle = normalizedCycles.find(cycle => cycle.id === cycleId)

    || normalizedCycles.find(cycle => cycle.status === TARGET_CYCLE_STATUS.ACTIVE)

    || normalizedCycles[0];

  const baseYearFromCycle = activeCycle?.baseYear || DEFAULT_BASE_YEAR;

  const currentYear = reportingCalendarYear();
  const initialBaseYear = mode === 'create' ? currentYear : baseYearFromCycle;
  const initialDrafts = useMemo(() => createTargetDrafts(
    mode === 'create' ? {} : (activeCycle?.targets || targets),
    inventories,
    initialBaseYear,
  ), [activeCycle, initialBaseYear, inventories, mode, targets]);

  const [drafts, setDrafts] = useState(initialDrafts);

  const [periodName, setPeriodName] = useState(() => (mode === 'create' ? '' : activeCycle?.name || activeCycle?.periodName || targets.periodName || `${baseYearFromCycle}–${activeCycle?.targetYear || 2030} reduction plan`));

  const [pathwayModes, setPathwayModes] = useState({ scope1: PATHWAY_MODES.AUTOMATIC, scope2: PATHWAY_MODES.AUTOMATIC, scope3: PATHWAY_MODES.AUTOMATIC });

  const [annualReductions, setAnnualReductions] = useState(() => buildAnnualReductions(initialDrafts));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const baseYear = drafts?.scope1?.baselineYear ?? '';

  const targetYear = drafts?.scope1?.targetYear ?? '';

  const baselineAvailable = TARGET_SCOPES.every(scope => baselineEmissionsFor(drafts[scope], inventories) !== null);

  const errorsByScope = useMemo(() => Object.fromEntries(TARGET_SCOPES.map(scope => [scope, drafts?.[scope] ? validateTargetDraft(drafts[scope], inventories) : []])), [drafts, inventories]);

  const allErrors = [...new Set(TARGET_SCOPES.flatMap(scope => errorsByScope[scope] || []))];

  const hasAnnualErrors = TARGET_SCOPES.some(scope => pathwayModes[scope] === PATHWAY_MODES.CUSTOM && pathwayYears(drafts).some(year => year > Number(drafts[scope].baselineYear) && (annualReductions[scope]?.[year] === '' || !Number.isFinite(Number(annualReductions[scope]?.[year])) || Number(annualReductions[scope]?.[year]) < 0 || Number(annualReductions[scope]?.[year]) > 100)));



  useEffect(() => {

    if (!open) return;

    const nextCycles = normalizeTargetCycles(targetCycles, targets);

    const nextActive = nextCycles.find(cycle => cycle.id === cycleId)

      || nextCycles.find(cycle => cycle.status === TARGET_CYCLE_STATUS.ACTIVE)

      || nextCycles[0];

    const nextBaseYear = mode === 'create'
      ? currentYear
      : (nextActive?.baseYear || DEFAULT_BASE_YEAR);

    const nextDrafts = createTargetDrafts(
      mode === 'create' ? {} : (nextActive?.targets || targets),
      inventories,
      nextBaseYear,
    );

    setDrafts(nextDrafts);

    setAnnualReductions(buildAnnualReductions(nextDrafts));

    setPathwayModes(Object.fromEntries(TARGET_SCOPES.map(scope => [
      scope,
      mode !== 'create' && nextActive?.targets?.[scope]?.pathwayMode === PATHWAY_MODES.CUSTOM
        ? PATHWAY_MODES.CUSTOM
        : PATHWAY_MODES.AUTOMATIC,
    ])));
    setSaving(false);
    setSaveError(null);

    setPeriodName(mode === 'create' ? '' : nextActive?.name || nextActive?.periodName || targets.periodName || `${nextBaseYear}–${nextActive?.targetYear || 2030} reduction plan`);

  }, [cycleId, currentYear, inventories, mode, open, targetCycles, targets]);



  const setReduction = (scope, value) => setDrafts(current => ({ ...current, [scope]: updateDraftReduction(current[scope], value, inventories) }));

  const setPathwayMode = (scope, mode) => {

    setPathwayModes(current => ({ ...current, [scope]: mode }));

    if (mode === PATHWAY_MODES.CUSTOM) setAnnualReductions(current => buildAnnualReductions(drafts, current));

  };

  const setAnnualReduction = (scope, year, value) => {

    setAnnualReductions(current => ({ ...current, [scope]: { ...current[scope], [year]: value } }));

    if (Number(year) === Number(targetYear)) setDrafts(current => ({ ...current, [scope]: { ...current[scope], reductionPercent: value } }));

  };

  const setTargetYear = value => setDrafts(current => {

    const next = Object.fromEntries(TARGET_SCOPES.map(scope => [scope, updateDraftTargetYear(current[scope], value, inventories)]));

    setAnnualReductions(previous => buildAnnualReductions(next, previous));

    return next;

  });

  const setBaselineYear = value => {

    const nextBaseYear = Number(value);

    setDrafts(current => {

      const next = Object.fromEntries(TARGET_SCOPES.map(scope => {

      const currentDraft = current[scope];

      const nextTargetYear = Number(currentDraft.targetYear) > nextBaseYear ? currentDraft.targetYear : nextBaseYear + 5;

      const nextDraft = { ...currentDraft, baselineYear: nextBaseYear, targetYear: nextTargetYear };

      return [scope, updateDraftReduction(nextDraft, currentDraft.reductionPercent, inventories)];

      }));

      setAnnualReductions(previous => buildAnnualReductions(next, previous));

      return next;

    });

  };

  const submit = async () => {

    if (saving || readOnly || allErrors.length > 0 || hasAnnualErrors || !periodName.trim()) return;

    const serialized = Object.fromEntries(TARGET_SCOPES.map(scope => [scope, {

      ...drafts[scope],

      baselineYear: Number(drafts[scope].baselineYear),

      targetYear: Number(drafts[scope].targetYear),

      reductionPercent: Number(drafts[scope].reductionPercent),

      targetYearEmissions: Number.isFinite(Number(drafts[scope].targetYearEmissions)) ? Number(drafts[scope].targetYearEmissions) : null,

      pathwayMode: pathwayModes[scope],

      annualReductionPercentages: pathwayModes[scope] === PATHWAY_MODES.CUSTOM ? annualReductions[scope] : null,

    }]));

    setSaving(true);
    setSaveError(null);
    try {
      await onSave({ ...serialized, periodName: periodName.trim(), createNewPeriod: mode === 'create' });
      onClose();
    } catch (error) {
      setSaveError(copy(t, 'common.feedback.save.failed', 'Unable to save target period.'));
    } finally {
      setSaving(false);
    }

  };


 return (

    <AppDialog fullWidth maxWidth={false} onClose={saving ? undefined : onClose} open={open} PaperProps={{ className: 'climate-action-dialog', sx: theme => ({ bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#FFFFFF', borderTop: 4, borderTopColor: 'success.main', maxHeight: 'calc(100vh - 24px)', maxWidth: 1080, width: 'calc(100vw - 24px)' }) }}>

      <DialogTitle sx={{ bgcolor: 'background.paper', fontSize: 22, fontWeight: 700, pb: 0.75, pr: 6 }}>

        {readOnly ? 'View emissions target' : t('climateAction.target.title')}

        <Typography color='text.secondary' variant='body2' sx={{ mt: 0.35, maxWidth: 850 }}>{copy(t, 'climateAction.target.subtitle', COPY.subtitle)}</Typography>

        <IconButton aria-label={t('common.close')} disabled={saving} onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}><CloseRoundedIcon fontSize='small' /></IconButton>

      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'background.default', overflowX: 'hidden', overflowY: 'auto', p: { xs: 1.25, md: 2 } }}>

        <Stack spacing={1.75}>

          <Paper variant='outlined' sx={{ bgcolor: 'background.paper', p: 1.5 }}>

            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.2fr) minmax(0, 0.8fr)' } }}>

              <LabeledField htmlFor='target-period-name' label={copy(t, 'climateAction.target.periodName', COPY.periodName)} required>

                <OutlinedInput disabled={readOnly} fullWidth id='target-period-name' onChange={event => setPeriodName(event.target.value)} placeholder='e.g. Near-term reduction plan 2025–2030' size='small' value={periodName} />

              </LabeledField>

              <Stack justifyContent='center' sx={{ px: { md: 1 } }}>

                <Stack alignItems='center' direction='row' spacing={0.45}>

                  <Typography color='text.secondary' variant='caption' sx={{ fontWeight: 650 }}>Baseline inventory</Typography>

                  <Tooltip arrow title='System-derived from the published inventory for the selected Baseline FY. It is not an approval status.'>

                    <InfoOutlinedIcon color='action' sx={{ fontSize: 15 }} />

                  </Tooltip>

                </Stack>

                <Stack alignItems='center' direction='row' spacing={0.7} sx={{ mt: 0.35 }}>

                  <Chip

                    color={baselineAvailable ? 'success' : 'warning'}

                    label={baselineAvailable

                      ? copy(t, 'climateAction.target.baselineStateAvailable', COPY.baselineAvailable)

                      : copy(t, 'climateAction.target.baselineStatePending', COPY.baselinePending)}

                    size='small'

                    sx={{ fontWeight: 700 }}

                    variant='outlined'

                  />

                </Stack>

                {!baselineAvailable && (

                  <Typography color='text.secondary' variant='caption' sx={{ display: 'block', mt: 0.45 }}>

                    {copy(t, 'climateAction.target.baselinePendingHint', COPY.baselinePendingHint)}

                  </Typography>

                )}

              </Stack>

              <Box sx={{ gridColumn: { md: '1 / -1' } }}>

                <PeriodSettings baseYear={baseYear} baselineYears={baselineYears} errors={allErrors} onBaselineYearChange={setBaselineYear} onTargetYearChange={setTargetYear} readOnly={readOnly} targetYear={targetYear} t={t} />

              </Box>

            </Box>

          </Paper>



          <ScopeReductionTable

            drafts={drafts}

            modes={pathwayModes}

            onModeChange={setPathwayMode}

            onReductionChange={setReduction}

            readOnly={readOnly}

            t={t}

          />



          <CustomAnnualTable annualReductions={annualReductions} drafts={drafts} modes={pathwayModes} onAnnualChange={setAnnualReduction} readOnly={readOnly} t={t} />

          <AbsolutePreview drafts={drafts} inventories={inventories} t={t} />

          {saveError && <Alert severity='error'>{saveError}</Alert>}

        </Stack>

      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>

        <Button color='inherit' disabled={saving} onClick={onClose}>{copy(t, 'common.cancel', COPY.cancel)}</Button>

        {!readOnly && <Button disabled={saving || !periodName.trim() || allErrors.length > 0 || hasAnnualErrors} onClick={submit} variant='contained'>{copy(t, 'climateAction.target.save', COPY.save)}</Button>}

      </DialogActions>

    </AppDialog>

  );

}
